/** @jsx isolateComponent */

/* eslint-disable import/no-duplicates */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { css } from '@emotion/core';

import { emitUserInteractionEvent } from '../../utils/avroEvents';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Icon, Link, Typography } from '@zapier/design-system';

import { Submenu } from './Submenu';
import { useUniversalNavContext } from '../../../universal-nav/hooks';

import type { UniversalNavLinkType } from '../../constants';
import {
  UNIVERSAL_TOPBAR_HEIGHT,
  UNIVERSAL_NAV_SUBMENU_ZINDEX,
} from '../../constants';

type Props = {
  /**
   * Content to render within the link.
   */
  children: ReactNode;
  /**
   * The `href` attribute to apply.
   */
  href: string | null;
  /**
   * Indicates whether the link is selected.
   */
  isSelected: boolean;
  /**
   * Links to display in the dropdown menu.
   */
  submenu?: Array<UniversalNavLinkType>;
};

const Styles = labelStyles('UniversalTopbarLink', {
  root: css`
    position: relative;
    display: flex;
    align-items: center;
    color: ${Colors.neutral700};
    font-size: 16px;
    line-height: 20px;
    
    &:hover,
    &:focus,
    &:focus-within {
      color: ${Colors.blue};
    },
  `,

  link: css`
    color: ${Colors.inherit};
    display: block;
    height: ${UNIVERSAL_TOPBAR_HEIGHT}px;
    line-height: ${UNIVERSAL_TOPBAR_HEIGHT}px;
    ${Typography.base};
    font-weight: 500;
    cursor: default;
    text-decoration: none;

    &:visited {
      color: inherit;
    }

    &:focus {
      outline: none;
    }

    &[href] {
      cursor: pointer;
    }
  `,

  details: css`
    display: flex;
    align-items: center;
  `,

  summary: css`
    /* Hides default icon on details element */
    &::-webkit-details-marker {
      display: none;
    }
    /* Hide default icon from IE. */
    &::before {
      display: none;
    }
  `,

  // Wrapper so layout works in Safari.
  summaryInner: css`
    display: flex;
    align-items: center;
  `,

  caret: css`
    margin-left: 3px;
  `,

  submenu: css`
    width: 400px;
    z-index: ${UNIVERSAL_NAV_SUBMENU_ZINDEX};
    position: absolute;
    left: 0;
    top: 100%;
    // This node needs to be at exactly "top: 100%" so that there isn't
    // a gap of even 1px between it and its toggle, otherwise hovering
    // off can occur in that gap and cause the menu to hide. However visually
    // the node needs to be pushed down 1px so that the topbar's bottom border
    // renders. Therefore we need a transparent 1px sliver that: 1) is part of
    // the node and thus fills the 1px gap caused by the topbar's bottom border,
    // 2) is transparent so the topbar's bottom border can still be seen,
    // even though it's overlapped by the node, and 3) push the content
    // within this node down by 1px so that its background doesn't overlap
    // the topbar's bottom border and cover it up. So there you have it,
    // one line of code with a 10 line explanation 😄
    border-top: 1px solid transparent;
    // Pad wrapper around Submenu to clip its shadow only on the top edge.
    // The shadow needs to be clipped because it's not (reasonably) possible
    // to have the node render beneath the topbar when it's a child of the topbar.
    // The padding values are based on the offset/blur/spread of the shadow.
    // This serves a dual purpose of also increasing the hover area of the
    // menu so that hovering a little bit off of it doesn't close the menu.
    padding: 0 40px 40px 40px;
    // Need to reposition the menu due to the transparent padding.
    margin-left: -40px;
    // Actually clip the shadow, again using padding as a sort of clip mask.
    overflow: hidden;
    // Padding should add to the width of the box in this case since it
    // isn't visual. Makes it a little easier to reason about dimensions.
    box-sizing: content-box;
  `,
});

/**
 * @private
 * Wrapper around `Link` specifically for use within `UniversalTopbar`.
 */
export const UniversalTopbarLink = (props: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const closeMenu = () => setIsMenuOpen(false);
  const openMenu = () => setIsMenuOpen(true);

  const isMenuOnly = props.href === null;

  const handleHover = () => {
    emitUserInteractionEvent({
      event_action: 'hover',
      option_selected: `UniversalLayout TopBar: ${
        isMenuOnly ? props.children : props.href
      }`,
    });
  };

  const handleClick = () => {
    emitUserInteractionEvent({
      event_action: 'click',
      option_selected: `UniversalLayout TopBar: ${
        isMenuOnly ? props.children : props.href
      }`,
    });
  };

  const LinkComponent = isMenuOnly ? 'span' : Link;
  const link = (
    <LinkComponent
      // @ts-ignore it doesn't matter if `color` is `undefined` on a `span`.
      color={isMenuOnly ? undefined : null}
      css={Styles.link}
      href={
        isMenuOnly ? undefined : getFullyQualifiedURL(props.href) || undefined
      }
      onMouseEnter={() => handleHover()}
      onClick={() => handleClick()}
    >
      {props.children}
    </LinkComponent>
  );

  const submenu = props.submenu && (
    <details css={Styles.details} open={isMenuOpen}>
      <summary aria-haspopup="menu" css={Styles.summary} tabIndex={0}>
        <div css={Styles.summaryInner}>
          {link}
          <span css={Styles.caret}>
            <Icon isBlock={true} name="arrowSmallDown" size={16} />
          </span>
        </div>
      </summary>
      <div css={Styles.submenu}>
        <Submenu links={props.submenu} />
      </div>
    </details>
  );

  return (
    <div css={Styles.root} onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      {submenu ? null : link}
      {submenu}
    </div>
  );
};

export default UniversalTopbarLink;
